import Phaser from 'phaser';

export class MenuScene extends Phaser.Scene {
  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Animated background with parallax grass
    this.createBackground(width, height);

    // Title
    const title = this.add.text(width / 2, height / 4, 'PlantWars', {
      fontFamily: 'monospace',
      fontSize: '48px',
      color: '#6aaa3a',
      stroke: '#2d1b69',
      strokeThickness: 6,
    });
    title.setOrigin(0.5, 0.5);

    // Subtitle
    const subtitle = this.add.text(width / 2, height / 4 + 50, 'Grow. Combine. Conquer.', {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#e8a038',
    });
    subtitle.setOrigin(0.5, 0.5);

    // Pulsing animation on title
    this.tweens.add({
      targets: title,
      scaleX: 1.05,
      scaleY: 1.05,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Menu buttons
    this.createButton(width / 2, height / 2 - 30, 'Story Mode', () => {
      this.scene.start('GameScene', { newGame: true, storyMode: true });
    });

    this.createButton(width / 2, height / 2 + 20, 'Free Play', () => {
      this.scene.start('GameScene', { newGame: true });
    });

    this.createButton(width / 2, height / 2 + 70, 'Battle Mode', () => {
      this.scene.start('ArenaScene');
    }, 0xd4573b, 0xff6666);

    this.createButton(width / 2, height / 2 + 120, 'Multiplayer', () => {
      this.showRoomJoinUI(width, height);
    });

    // Version text
    this.add.text(width - 10, height - 10, 'v0.4.0', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#666',
    }).setOrigin(1, 1);

    // Decorative plants on the sides
    this.addDecorativePlants(width, height);
  }

  private createBackground(width: number, height: number): void {
    // Gradient background using graphics
    const bg = this.add.graphics();

    // Sky gradient (top to bottom)
    for (let y = 0; y < height; y++) {
      const t = y / height;
      const r = Math.floor(26 + t * 20);
      const g = Math.floor(26 + t * 40);
      const b = Math.floor(46 + t * 30);
      bg.fillStyle(Phaser.Display.Color.GetColor(r, g, b));
      bg.fillRect(0, y, width, 1);
    }

    // Stars
    for (let i = 0; i < 50; i++) {
      const x = Phaser.Math.Between(0, width);
      const y = Phaser.Math.Between(0, height / 2);
      const size = Phaser.Math.Between(1, 2);
      const alpha = Phaser.Math.FloatBetween(0.3, 0.8);
      const star = this.add.circle(x, y, size, 0xffffff, alpha);

      // Twinkle
      this.tweens.add({
        targets: star,
        alpha: alpha * 0.3,
        duration: Phaser.Math.Between(1000, 3000),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Ground
    bg.fillStyle(0x3e6139);
    bg.fillRect(0, height * 0.75, width, height * 0.25);
    bg.fillStyle(0x4a7c4b);
    bg.fillRect(0, height * 0.75, width, 4);
  }

  private createButton(
    x: number, y: number, text: string, callback: () => void,
    fillColor: number = 0x4a7c4b, hoverColor: number = 0x6aaa3a,
  ): void {
    const bg = this.add.graphics();
    const w = 200;
    const h = 40;
    const strokeColor = hoverColor;
    const hoverStroke = Phaser.Display.Color.ValueToColor(hoverColor).lighten(30).color;

    bg.fillStyle(fillColor, 1);
    bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, 4);
    bg.lineStyle(2, strokeColor, 1);
    bg.strokeRoundedRect(x - w / 2, y - h / 2, w, h, 4);

    const label = this.add.text(x, y, text, {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: '#ffffff',
    });
    label.setOrigin(0.5, 0.5);

    // Make interactive
    const hitArea = this.add.zone(x, y, w, h).setInteractive({ useHandCursor: true });

    hitArea.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(hoverColor, 1);
      bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, 4);
      bg.lineStyle(2, hoverStroke, 1);
      bg.strokeRoundedRect(x - w / 2, y - h / 2, w, h, 4);
      label.setColor('#ffffff');
    });

    hitArea.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(fillColor, 1);
      bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, 4);
      bg.lineStyle(2, strokeColor, 1);
      bg.strokeRoundedRect(x - w / 2, y - h / 2, w, h, 4);
      label.setColor('#ffffff');
    });

    hitArea.on('pointerdown', callback);
  }

  private addDecorativePlants(width: number, height: number): void {
    // Add some animated plant sprites on the ground level
    const groundY = height * 0.75;

    // Left side plants
    for (let i = 0; i < 5; i++) {
      const x = Phaser.Math.Between(20, width / 4);
      const y = groundY - Phaser.Math.Between(5, 15);
      const plant = this.add.sprite(x, y, 'plants', Phaser.Math.Between(0, 5) * 4 + 3);
      plant.setScale(2);

      // Gentle sway
      this.tweens.add({
        targets: plant,
        angle: Phaser.Math.Between(-3, 3),
        duration: Phaser.Math.Between(2000, 4000),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Right side plants
    for (let i = 0; i < 5; i++) {
      const x = Phaser.Math.Between(width * 0.75, width - 20);
      const y = groundY - Phaser.Math.Between(5, 15);
      const plant = this.add.sprite(x, y, 'plants', Phaser.Math.Between(0, 5) * 4 + 3);
      plant.setScale(2);

      this.tweens.add({
        targets: plant,
        angle: Phaser.Math.Between(-3, 3),
        duration: Phaser.Math.Between(2000, 4000),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private showRoomJoinUI(width: number, height: number): void {
    const overlay = this.add.container(width / 2, height / 2);
    overlay.setDepth(100);

    const panelW = 320;
    const panelH = 200;

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x0a0a1e, 0.95);
    bg.fillRoundedRect(-panelW / 2, -panelH / 2, panelW, panelH, 8);
    bg.lineStyle(2, 0x4a7c4b);
    bg.strokeRoundedRect(-panelW / 2, -panelH / 2, panelW, panelH, 8);
    overlay.add(bg);

    // Title
    const title = this.add.text(0, -panelH / 2 + 15, 'Join Multiplayer', {
      fontFamily: 'monospace', fontSize: '16px', color: '#6aaa3a', fontStyle: 'bold',
    });
    title.setOrigin(0.5, 0);
    overlay.add(title);

    // Instructions
    const info = this.add.text(0, -panelH / 2 + 40, 'Share a room code with friends\nor create a new room:', {
      fontFamily: 'monospace', fontSize: '10px', color: '#aaaaaa', align: 'center',
    });
    info.setOrigin(0.5, 0);
    overlay.add(info);

    // Create a DOM text input for room code
    const inputElement = document.createElement('input');
    inputElement.type = 'text';
    inputElement.placeholder = 'Enter room code...';
    inputElement.maxLength = 30;
    inputElement.style.cssText = 'font-family:monospace;font-size:14px;padding:8px 12px;width:200px;background:#1a1a2e;color:#ffffff;border:2px solid #4a7c4b;border-radius:4px;outline:none;text-align:center;';
    const domInput = this.add.dom(0, -10, inputElement);
    overlay.add(domInput);

    // "Join" button
    const joinBtnBg = this.add.graphics();
    joinBtnBg.fillStyle(0x4a7c4b, 1);
    joinBtnBg.fillRoundedRect(-70, 30, 140, 36, 4);
    joinBtnBg.lineStyle(1, 0x6aaa3a);
    joinBtnBg.strokeRoundedRect(-70, 30, 140, 36, 4);
    overlay.add(joinBtnBg);

    const joinLabel = this.add.text(0, 48, 'Join Room', {
      fontFamily: 'monospace', fontSize: '14px', color: '#ffffff', fontStyle: 'bold',
    });
    joinLabel.setOrigin(0.5, 0.5);
    overlay.add(joinLabel);

    const joinHit = this.add.zone(0, 48, 140, 36).setInteractive({ useHandCursor: true });
    overlay.add(joinHit);

    const startMultiplayer = (roomCode: string) => {
      const finalRoomId = roomCode.trim() || `room-${Date.now().toString(36)}`;
      inputElement.remove();
      overlay.destroy();
      this.scene.start('GameScene', { multiplayer: true, roomId: finalRoomId });
    };

    joinHit.on('pointerdown', () => {
      startMultiplayer(inputElement.value);
    });

    // Handle Enter key on the input
    inputElement.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        startMultiplayer(inputElement.value);
      }
    });

    // "Quick Play" button (random room)
    const quickBtnBg = this.add.graphics();
    quickBtnBg.fillStyle(0x333333, 1);
    quickBtnBg.fillRoundedRect(-70, 70, 140, 28, 4);
    overlay.add(quickBtnBg);

    const quickLabel = this.add.text(0, 84, 'Quick Play', {
      fontFamily: 'monospace', fontSize: '11px', color: '#aaaaaa',
    });
    quickLabel.setOrigin(0.5, 0.5);
    overlay.add(quickLabel);

    const quickHit = this.add.zone(0, 84, 140, 28).setInteractive({ useHandCursor: true });
    overlay.add(quickHit);
    quickHit.on('pointerdown', () => {
      startMultiplayer('');
    });

    // Close button
    const closeBtnBg = this.add.circle(panelW / 2 - 12, -panelH / 2 + 12, 14, 0xd4573b, 0.9);
    overlay.add(closeBtnBg);
    const closeX = this.add.text(panelW / 2 - 12, -panelH / 2 + 12, 'X', {
      fontFamily: 'monospace', fontSize: '14px', color: '#ffffff', fontStyle: 'bold',
    });
    closeX.setOrigin(0.5, 0.5);
    overlay.add(closeX);
    const closeHit = this.add.circle(panelW / 2 - 12, -panelH / 2 + 12, 18, 0x000000, 0.001);
    closeHit.setInteractive();
    closeHit.on('pointerdown', () => {
      inputElement.remove();
      overlay.destroy();
    });
    overlay.add(closeHit);

    // Focus input
    inputElement.focus();
  }
}
